import { useCallback, useMemo, useRef } from 'react';
import { trackEvent } from 'analytics';

import { WORKFLOW_EVENTS, type WorkflowSource } from './events';
import { useAnalyticsCompanyContext } from './useAnalyticsCompanyContext';

import type {
  EWorkflow,
  WorkflowCompletedProperties,
  WorkflowContextBase,
  WorkflowDetails,
  WorkflowEventProperties,
  WorkflowStartedProperties
} from './types';

export type WorkflowTracker = {
  /** Fire when the input opens (the action button press). */
  trackStarted: (details?: WorkflowDetails) => void;
  /**
   * Fire when the user presses confirm. Times the request for
   * `submit_duration_ms`; only emits an event when the page defines one.
   */
  trackSubmitted: () => void;
  /** Fire when the request resolves, with `success` from the backend. */
  trackCompleted: (success: boolean, details?: WorkflowDetails) => void;
  /**
   * Wire into the input's open/close callback. Fires `abandoned` on a close
   * that follows a `started` with no `completed` in between — so cancels are
   * tracked but error-closes (which already emit `completed(false)`) are not.
   * A close while a submit is in flight (confirm dialogs close on confirm) is
   * not an abandon: its `completed` still follows.
   */
  onModalStateChange: (isOpen: boolean) => void;
  /**
   * Wraps a request: fires `submitted`, then `completed` with its outcome.
   * Resolves or rejects like `request`, so callers keep their own handling.
   */
  trackRequest: <T>(
    request: Promise<T>,
    details?: WorkflowDetails
  ) => Promise<T>;
};

/**
 * Encapsulates the started → completed / abandoned lifecycle for a single
 * input flow, including the abandoned guard. The context is read through a
 * ref so the returned callbacks keep a stable identity across renders.
 */
export function useWorkflowTracker(
  source: WorkflowSource,
  workflowType: EWorkflow,
  context: WorkflowContextBase = {}
): WorkflowTracker {
  const company = useAnalyticsCompanyContext();
  const contextRef = useRef<WorkflowContextBase>(context);
  contextRef.current = { ...company, ...context };
  const events = WORKFLOW_EVENTS[source];
  const startedRef = useRef(false);
  const completedRef = useRef(false);
  const detailsRef = useRef<WorkflowDetails>({});
  const submittedAtRef = useRef<number | null>(null);

  const build = useCallback(
    (details?: WorkflowDetails): WorkflowEventProperties => ({
      ...contextRef.current,
      ...detailsRef.current,
      ...details,
      workflow_type: workflowType
    }),
    [workflowType]
  );

  const trackStarted = useCallback(
    (details?: WorkflowDetails) => {
      startedRef.current = true;
      completedRef.current = false;
      submittedAtRef.current = null;
      detailsRef.current = details ?? {};
      const properties: WorkflowStartedProperties = {
        ...build(),
        timestamp: new Date().toISOString()
      };
      trackEvent(events.started, properties);
    },
    [build, events]
  );

  const trackSubmitted = useCallback(() => {
    submittedAtRef.current = Date.now();
    const { submitted } = events;
    if (submitted) {
      trackEvent(submitted, build());
    }
  }, [build, events]);

  const trackCompleted = useCallback(
    (success: boolean, details?: WorkflowDetails) => {
      completedRef.current = true;
      const submittedAt = submittedAtRef.current;
      submittedAtRef.current = null;
      const properties: WorkflowCompletedProperties = {
        ...build(details),
        success,
        ...(submittedAt !== null && {
          submit_duration_ms: Date.now() - submittedAt
        })
      };
      trackEvent(events.completed, properties);
    },
    [build, events]
  );

  const onModalStateChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen || !startedRef.current) return;
      startedRef.current = false;
      if (completedRef.current) return;
      if (submittedAtRef.current !== null) return;
      trackEvent(events.abandoned, build());
    },
    [build, events]
  );

  const trackRequest = useCallback(
    async function trackRequest<T>(
      request: Promise<T>,
      details?: WorkflowDetails
    ) {
      trackSubmitted();
      try {
        const result = await request;
        trackCompleted(true, details);
        return result;
      } catch (error) {
        trackCompleted(false, details);
        throw error;
      }
    },
    [trackSubmitted, trackCompleted]
  );

  return useMemo(
    () => ({
      trackStarted,
      trackSubmitted,
      trackCompleted,
      onModalStateChange,
      trackRequest
    }),
    [
      trackStarted,
      trackSubmitted,
      trackCompleted,
      onModalStateChange,
      trackRequest
    ]
  );
}
